import {
  CopyIcon,
  DownloadSimpleIcon,
  FunnelSimpleIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import type { AnalysisReport, GitignoreSourceInput } from '../types';
import { useI18n } from '../i18n';
import { StatusBadge } from './StatusBadge';

interface RulesPanelProps {
  readonly report: AnalysisReport | null;
  readonly sources: readonly GitignoreSourceInput[];
  readonly selectedSource: string;
  readonly selectedRuleId: string | null;
  readonly onSourceSelect: (path: string) => void;
  readonly onSourceContentChange: (path: string, content: string) => void;
  readonly onRuleSelect: (ruleId: string | null) => void;
  readonly onCopySource: () => void;
  readonly onDownloadSource: () => void;
}

export function RulesPanel({
  report,
  sources,
  selectedSource,
  selectedRuleId,
  onSourceSelect,
  onSourceContentChange,
  onRuleSelect,
  onCopySource,
  onDownloadSource,
}: RulesPanelProps) {
  const { t } = useI18n();
  const activeSource = sources.find((source) => source.path === selectedSource) ?? sources[0];
  const rules = report?.rules.filter((rule) => rule.source === activeSource?.path) ?? [];

  return (
    <section id="panel-rules" className="panel rules-panel" aria-labelledby="rules-heading">
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">{t('panels.rules.kicker')}</p>
          <h2 id="rules-heading">{t('panels.rules.title')}</h2>
        </div>
        <span
          className="panel-count"
          aria-label={t('panels.rules.countLabel', { count: rules.length })}
        >
          {rules.length}
        </span>
      </div>

      <div className="source-toolbar">
        <label>
          <span className="visually-hidden">{t('panels.rules.source')}</span>
          <select
            value={activeSource?.path ?? ''}
            onChange={(event) => {
              onSourceSelect(event.currentTarget.value);
            }}
          >
            {sources.map((source) => (
              <option key={source.path} value={source.path}>
                {source.path}
              </option>
            ))}
          </select>
        </label>
        <div className="toolbar-actions">
          <button
            className="icon-button icon-button-small"
            type="button"
            aria-label={t('actions.copyGitignore')}
            onClick={onCopySource}
            disabled={!activeSource}
          >
            <CopyIcon aria-hidden="true" />
          </button>
          <button
            className="icon-button icon-button-small"
            type="button"
            aria-label={t('actions.downloadGitignore')}
            onClick={onDownloadSource}
            disabled={!activeSource}
          >
            <DownloadSimpleIcon aria-hidden="true" />
          </button>
        </div>
      </div>

      <label className="editor-field">
        <span className="field-label">
          <PencilSimpleIcon aria-hidden="true" /> {t('panels.rules.editorLabel')}
        </span>
        <textarea
          spellCheck={false}
          value={activeSource?.content ?? ''}
          onChange={(event) => {
            if (activeSource) onSourceContentChange(activeSource.path, event.currentTarget.value);
          }}
          aria-describedby="rules-editor-help"
        />
        <small id="rules-editor-help">{t('panels.rules.editorHelp')}</small>
      </label>

      <div className="subsection-heading">
        <h3>{t('panels.rules.diagnostics')}</h3>
        {selectedRuleId ? (
          <button
            className="text-button"
            type="button"
            onClick={() => {
              onRuleSelect(null);
            }}
          >
            <FunnelSimpleIcon aria-hidden="true" /> {t('actions.clearRuleFilter')}
          </button>
        ) : null}
      </div>

      <div className="rule-list" role="list" aria-label={t('panels.rules.diagnostics')}>
        {rules.map((rule) => (
          <button
            className={`rule-row${selectedRuleId === rule.id ? ' is-selected' : ''}`}
            type="button"
            role="listitem"
            key={rule.id}
            aria-pressed={selectedRuleId === rule.id}
            onClick={() => {
              onRuleSelect(selectedRuleId === rule.id ? null : rule.id);
            }}
          >
            <span className="rule-line">{rule.line}</span>
            <span className="rule-body">
              <code className={rule.kind !== 'pattern' ? 'rule-muted' : ''}>
                {rule.raw || t('panels.rules.blankRule')}
              </code>
              <span className="rule-meta">
                <StatusBadge status={rule.status} />
                <span>{t('panels.rules.matches', { count: rule.matchCount })}</span>
              </span>
            </span>
          </button>
        ))}
        {rules.length === 0 ? (
          <div className="empty-inline">
            <p>{t('panels.rules.empty')}</p>
            <small>{t('panels.rules.emptyHelp')}</small>
          </div>
        ) : null}
      </div>
    </section>
  );
}
